"use client";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { ThumbsUp, ThumbsDown, Share2, MessageSquare } from "lucide-react";

export type ReactionType = "like" | "dislike" | null;

type VideoActionsProps = {
    identity: string;
    initialLikesCount: number;
    initialDislikesCount: number;
    initialMyReaction?: ReactionType;
    onChange?: (payload: {
        likesCount: number;
        dislikesCount: number;
        myReaction: ReactionType;
    }) => void;
};

type ReactionsState = {
    likesCount: number;
    dislikesCount: number;
    myReaction: ReactionType;
};

type VideoActionsResponse = {
    likes_count?: number;
    dislikes_count?: number;
    my_reaction?: unknown;
};

const API_BASE_URL = process.env.NEXT_PUBLIC_BASE_API_URL;

function getReactionUrl(identity: string) {
    const encodedIdentity = encodeURIComponent(identity);
    return `${API_BASE_URL}/api/v1/video/${encodedIdentity}/reaction`;
}

async function setReaction(identity: string, type: "like" | "dislike") {
    const res = await fetch(getReactionUrl(identity), {
        method: "POST",
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ type }),
    });

    if (!res.ok) {
        throw new Error("Failed to set reaction");
    }
}

async function removeReaction(identity: string) {
    const res = await fetch(getReactionUrl(identity), {
        method: "DELETE",
        credentials: "include",
    });

    if (!res.ok) {
        throw new Error("Failed to remove reaction");
    }
}

async function fetchVideoActions(identity: string): Promise<VideoActionsResponse | null> {
    const encodedIdentity = encodeURIComponent(identity);
    const res = await fetch(`${API_BASE_URL}/api/v1/video/${encodedIdentity}`, {
        credentials: "include",
        cache: "no-store",
    });

    if (!res.ok) return null;
    const json = await res.json();
    return json?.data ?? null;
}

function normalizeCount(value: number) {
    return Math.max(0, value);
}

function normalizeReaction(value: unknown): ReactionType {
    if (typeof value !== "string") return null;

    const normalized = value.trim().toLowerCase();
    if (normalized === "like" || normalized === "liked") return "like";
    if (normalized === "dislike" || normalized === "disliked") return "dislike";
    return null;
}

function VideoActions({
    identity,
    initialLikesCount,
    initialDislikesCount,
    initialMyReaction = null,
    onChange,
}: VideoActionsProps) {
    const initialReaction = useMemo(
        () => normalizeReaction(initialMyReaction),
        [initialMyReaction],
    );
    const [likesCount, setLikesCount] = useState(normalizeCount(initialLikesCount));
    const [dislikesCount, setDislikesCount] = useState(
        normalizeCount(initialDislikesCount),
    );
    const [myReaction, setMyReaction] = useState<ReactionType>(initialReaction);
    const [loading, setLoading] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [copied, setCopied] = useState(false);
    const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setLikesCount(normalizeCount(initialLikesCount));
        setDislikesCount(normalizeCount(initialDislikesCount));
        setMyReaction(initialReaction);
    }, [identity, initialLikesCount, initialDislikesCount, initialReaction]);

    useEffect(() => {
        if (initialReaction !== null) return;

        let cancelled = false;
        const syncInitialReaction = async () => {
            try {
                const data = await fetchVideoActions(identity);
                if (!data || cancelled) return;

                const nextReaction = normalizeReaction(data.my_reaction);
                if (nextReaction === null) return;

                setMyReaction(nextReaction);
                if (typeof data.likes_count === "number") {
                    setLikesCount(normalizeCount(data.likes_count));
                }
                if (typeof data.dislikes_count === "number") {
                    setDislikesCount(normalizeCount(data.dislikes_count));
                }
            } catch {
                // ignore: keep server-provided defaults
            }
        };

        syncInitialReaction();
        return () => {
            cancelled = true;
        };
    }, [identity, initialReaction]);

    useEffect(() => {
        return () => {
            if (copiedTimeoutRef.current) {
                clearTimeout(copiedTimeoutRef.current);
            }
        };
    }, []);

    const emitChange = useCallback((state: ReactionsState) => {
        onChange?.(state);
    }, [onChange]);

    const handleLike = useCallback(async () => {
        if (loading) return;

        setLoading(true);
        const previousState: ReactionsState = { likesCount, dislikesCount, myReaction };

        let nextState: ReactionsState;
        if (myReaction === "like") {
            nextState = {
                likesCount: normalizeCount(likesCount - 1),
                dislikesCount,
                myReaction: null,
            };
        } else if (myReaction === "dislike") {
            nextState = {
                likesCount: likesCount + 1,
                dislikesCount: normalizeCount(dislikesCount - 1),
                myReaction: "like",
            };
        } else {
            nextState = {
                likesCount: likesCount + 1,
                dislikesCount,
                myReaction: "like",
            };
        }

        setLikesCount(nextState.likesCount);
        setDislikesCount(nextState.dislikesCount);
        setMyReaction(nextState.myReaction);
        emitChange(nextState);

        try {
            if (previousState.myReaction === "like") {
                await removeReaction(identity);
            } else {
                await setReaction(identity, "like");
            }
        } catch {
            setLikesCount(previousState.likesCount);
            setDislikesCount(previousState.dislikesCount);
            setMyReaction(previousState.myReaction);
            emitChange(previousState);
        } finally {
            setLoading(false);
        }
    }, [dislikesCount, emitChange, identity, likesCount, loading, myReaction]);

    const handleDislike = useCallback(async () => {
        if (loading) return;

        setLoading(true);
        const previousState: ReactionsState = { likesCount, dislikesCount, myReaction };

        let nextState: ReactionsState;
        if (myReaction === "dislike") {
            nextState = {
                likesCount,
                dislikesCount: normalizeCount(dislikesCount - 1),
                myReaction: null,
            };
        } else if (myReaction === "like") {
            nextState = {
                likesCount: normalizeCount(likesCount - 1),
                dislikesCount: dislikesCount + 1,
                myReaction: "dislike",
            };
        } else {
            nextState = {
                likesCount,
                dislikesCount: dislikesCount + 1,
                myReaction: "dislike",
            };
        }

        setLikesCount(nextState.likesCount);
        setDislikesCount(nextState.dislikesCount);
        setMyReaction(nextState.myReaction);
        emitChange(nextState);

        try {
            if (previousState.myReaction === "dislike") {
                await removeReaction(identity);
            } else {
                await setReaction(identity, "dislike");
            }
        } catch {
            setLikesCount(previousState.likesCount);
            setDislikesCount(previousState.dislikesCount);
            setMyReaction(previousState.myReaction);
            emitChange(previousState);
        } finally {
            setLoading(false);
        }
    }, [dislikesCount, emitChange, identity, likesCount, loading, myReaction]);

    const handleShare = useCallback(async () => {
        if (sharing) return;

        setSharing(true);
        try {
            const url = window.location.href;
            const isMobile =
                window.matchMedia("(pointer: coarse)").matches ||
                /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

            if (isMobile && navigator.share) {
                await navigator.share({
                    title: document.title,
                    url,
                });
            } else {
                await navigator.clipboard.writeText(url);
                setCopied(true);
                if (copiedTimeoutRef.current) {
                    clearTimeout(copiedTimeoutRef.current);
                }
                copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
            }
        } finally {
            setSharing(false);
        }
    }, [sharing]);

    const handleScrollToComments = useCallback(() => {
        const commentsSection = document.getElementById("comments");
        if (!commentsSection) return;

        const isMobile = window.matchMedia("(max-width:899.95px)").matches;
        const stickyPlayer = document.querySelector<HTMLElement>('[data-video-mobile-sticky="true"]');
        const stickyOffset = (isMobile && stickyPlayer) ? (stickyPlayer.getBoundingClientRect().height + 28) : 0;
        const targetTop = commentsSection.getBoundingClientRect().top + window.scrollY - stickyOffset;

        window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: "smooth",
        });
    }, []);

    return (
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button
                color={copied ? "success" : "primary"}
                variant="outlined"
                size="small"
                startIcon={<Share2 size={16} />}
                disabled={sharing}
                onClick={handleShare}
                sx={{
                    height: { xs: 30, sm: 34 },
                    minWidth: { xs: 96, sm: 120 },
                    borderRadius: 2,
                    px: { xs: 1, sm: 1.5 },
                }}
            >
                <Typography component="span" sx={{
                    fontSize: { xs: 12, sm: 14 },
                    fontWeight: 500,
                    minWidth: { xs: 66, sm: 85 },
                    textAlign: 'center',
                    pr: 1,
                }}>
                    {copied ? "لینک کپی شد" : "اشتراک گزاری"}
                </Typography>
            </Button>
            <Button
                color="inherit"
                variant="outlined"
                size="small"
                startIcon={<MessageSquare size={16} />}
                onClick={handleScrollToComments}
                sx={{
                    height: { xs: 30, sm: 34 },
                    minWidth: { xs: 74, sm: 96 },
                    borderRadius: 2,
                    px: { xs: 1, sm: 1.5 },
                }}
            >
                <Typography component="span" sx={{
                    fontSize: { xs: 12, sm: 14 },
                    fontWeight: 500,
                    minWidth: { xs: 46, sm: 56 },
                    textAlign: "center",
                    pr: 1,
                }}>
                    نظرات
                </Typography>
            </Button>
            <Box sx={{
                height: { xs: 30, sm: 34 },
                borderRadius: 2,
                display: 'flex',
                border: '1px solid',
                borderColor: 'divider',
                p: { xs: 0.25, sm: 0.5 },
                px: { xs: 1, sm: 1.5 }
            }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box
                        component="button"
                        type="button"
                        disabled={loading}
                        onClick={handleDislike}
                        sx={{
                            all: "unset",
                            display: "inline-flex",
                            alignItems: "center",
                            minWidth: { xs: 30, sm: 36 },
                            justifyContent: 'center',
                            cursor: loading ? "not-allowed" : "pointer",
                            color: myReaction === "dislike" ? "error.main" : "text.primary",
                            opacity: loading ? 0.5 : 1,
                        }}
                    >
                        <ThumbsDown size={18} />
                    </Box>
                    <Divider flexItem orientation="vertical" />

                    <Stack direction="row" alignItems="center" spacing={0.25} onClick={handleLike} sx={{
                        cursor: loading ? "not-allowed" : "pointer",
                    }} >
                        <Box component="span" sx={{ fontFeatureSettings: '"ss05"', minWidth: 16, textAlign: "center" }}>
                            {likesCount}
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            disabled={loading}
                            sx={{
                                all: "unset",
                                display: "inline-flex",
                                alignItems: "center",
                                cursor: loading ? "not-allowed" : "pointer",
                                color: myReaction === "like" ? "success.main" : "text.primary",
                                opacity: loading ? 0.5 : 1,
                            }}
                        >
                            <ThumbsUp size={18} />
                        </Box>

                    </Stack>
                    {/* <Box sx={{ width: 1, height: 16, bgcolor: "divider" }} /> */}

                </Stack>
            </Box>
        </Stack>
    );
}

export default memo(VideoActions);
